import i18n from "../../util/i18n.ts";
import { useEffect, useRef, useState } from "react";
import { logEvent } from "../../util/logEvent.ts";
import type { LeagueInfo } from "../../../../../common/types/newLeague.ts";
import { choice } from "../../../../../common/random.ts";

const LeagueMenu = <Value extends string>({
  getLeagueInfo,
  onDone,
  onLoading,
  quickValues,
  value,
  values,
  value2,
  values2,
  onNewValue2,
}: {
  getLeagueInfo: (value: Value, value2: number) => Promise<LeagueInfo>;
  onDone: (leagueInfo: any) => void;
  onLoading: (value: Value, value2: number) => void;
  quickValues?: Value[];
  value: Value;
  values: { key: Value; value: string }[];
  value2?: number;
  values2?: { key: number; value: string }[];
  onNewValue2?: (key: number) => void;
}) => {
  const waitingForInfo = useRef<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const handleNewValue = async (newValue: Value, value2?: number) => {
    const nextValue2 = value2 ?? 0;
    const requestKey = `${newValue}:${nextValue2}`;
    waitingForInfo.current = requestKey;
    setLoading(true);
    onLoading(newValue, nextValue2);

    try {
      const leagueInfo = await getLeagueInfo(newValue, nextValue2);
      if (waitingForInfo.current === requestKey) {
        onDone(leagueInfo);
      }
    } catch (error) {
      console.error(error);
      logEvent({
        type: "error",
        text: `Error loading real team data: ${error.message}`,
        saveToDb: false,
        persistent: true,
      });
    }
    if (waitingForInfo.current === requestKey) {
      waitingForInfo.current = undefined;
      setLoading(false);
    }
  };

  // Handle initial value
  useEffect(() => {
    handleNewValue(value, value2);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const valueChoiceKeys = [
    value,
    ...(quickValues ?? values.slice(0, 8).map((option) => option.key)),
  ].filter((key, index, all) => all.indexOf(key) === index);
  const valueChoices = valueChoiceKeys
    .map((key) => values.find((option) => option.key === key))
    .filter((option): option is { key: Value; value: string } => option !== undefined);

  return (
    <>
      <div className="mb-2">
        <div className="form-label mb-1">
          {i18n.t("newLeague.menuSeason", { ns: "ui", defaultValue: "Season" })}
        </div>
        <div className="d-flex flex-wrap gap-1" data-new-league-season-choices>
          {valueChoices.map(({ key, value: label }) => (
            <button
              key={key}
              type="button"
              className={
                key === value ? "btn btn-primary btn-sm" : "btn btn-outline-secondary btn-sm"
              }
              data-new-league-season-choice={key}
              disabled={loading}
              onClick={() => {
                void handleNewValue(key, value2);
              }}
            >
              {label}
            </button>
          ))}
        </div>
        {quickValues ? (
          <details className="mt-2" data-native-details-host="new-league-all-seasons">
            <summary className="btn btn-outline-secondary btn-sm">
              {i18n.t("newLeague.allSeasons", {
                ns: "ui",
                defaultValue: "All seasons",
              })}
            </summary>
            <div className="d-flex flex-wrap gap-1 mt-2" data-new-league-all-season-choices>
              {values.map(({ key, value: label }) => (
                <button
                  key={key}
                  type="button"
                  className={
                    key === value ? "btn btn-primary btn-sm" : "btn btn-outline-secondary btn-sm"
                  }
                  data-new-league-season-choice={key}
                  disabled={loading}
                  onClick={() => {
                    void handleNewValue(key, value2);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </details>
        ) : null}
      </div>
      {value2 !== undefined && values2 && onNewValue2 ? (
        <div className="mb-2">
          <div className="form-label mb-1">
            {i18n.t("newLeague.menuPhase", { ns: "ui", defaultValue: "Phase" })}
          </div>
          <div className="d-flex flex-wrap gap-1" data-new-league-phase-choices>
            {values2.map(({ key, value: label }) => (
              <button
                key={key}
                type="button"
                className={
                  key === value2 ? "btn btn-primary btn-sm" : "btn btn-outline-secondary btn-sm"
                }
                data-new-league-phase-choice={key}
                disabled={loading}
                onClick={() => {
                  onNewValue2(key);
                  void handleNewValue(value, key);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      <div className="mb-1">
        <button
          className="btn btn-outline-secondary btn-sm"
          disabled={loading}
          type="button"
          onClick={() => {
            const keys = values.map((v) => v.key);
            const random = choice(keys);

            if (value2 !== undefined && values2 && onNewValue2) {
              const keys2 = values2.map((v) => v.key);
              const random2 = choice(keys2);
              onNewValue2(random2);
            }

            handleNewValue(random, value2);
          }}
        >
          {i18n.t("common.random", { ns: "ui", defaultValue: "Random" })}
        </button>
      </div>
      {loading ? (
        <div aria-busy="true" data-new-league-menu-loading="" role="status">
          {i18n.t("newLeague.loadingOfficialData", {
            ns: "ui",
            defaultValue: "Loading official team data…",
          })}
        </div>
      ) : (
        <span className="visually-hidden" data-new-league-menu-ready="">
          {i18n.t("newLeague.officialDataReady", {
            ns: "ui",
            defaultValue: "Official team data ready",
          })}
        </span>
      )}
    </>
  );
};

export default LeagueMenu;
